import { Link } from '@tanstack/react-router'
import { hitTarget, type SearchHit, type SearchKind } from '@/entities/search'
import { cn } from '@/shared/lib'
import { Icon, type IconName } from '@/shared/ui/icon'

interface HitRowProps {
  hit: SearchHit
  active?: boolean
  onSelect?: () => void
}

// Иконка — только там, где она несёт смысл: встреча и найденное в расшифровке.
// Справочные виды (компания/проект/тег) различаются меткой справа.
const kindIcon: Partial<Record<SearchKind, IconName>> = {
  meeting: 'camera',
  transcript: 'chat',
}

const kindLabel: Record<SearchKind, string> = {
  meeting: 'Встреча',
  transcript: 'В расшифровке',
  company: 'Компания',
  project: 'Проект',
  tag: 'Тег',
}

// Строка выдачи палитры: вид сущности + метка «почему в выдаче». Совпадение по
// названию видно в самой строке, по сказанному на встрече или по тегу — нет,
// поэтому под строкой сниппет. Подсветку ts_headline не рендерим как HTML —
// показываем текстом.
export function HitRow({ hit, active = false, onSelect }: HitRowProps) {
  const snippet = hit.snippet?.replace(/<\/?b>/g, '')
  const icon = kindIcon[hit.kind]
  return (
    <Link
      {...hitTarget(hit)}
      onClick={onSelect}
      className={cn(
        'block rounded-control px-3 py-2 transition-colors',
        active ? 'bg-bg-elevated' : 'hover:bg-bg-elevated',
      )}
    >
      <div className="flex items-center gap-2">
        {icon ? (
          <Icon name={icon} size={14} className="shrink-0 text-text-tertiary" />
        ) : (
          <span aria-hidden className="w-3.5 shrink-0" />
        )}
        <span className="min-w-0 flex-1 truncate text-sm text-text-primary">{hit.label}</span>
        <span className="shrink-0 text-xs text-text-tertiary">{kindLabel[hit.kind]}</span>
      </div>
      {snippet && hit.matchedIn !== 'title' && hit.matchedIn !== 'name' && (
        <p className="mt-0.5 truncate pl-6 text-xs text-text-tertiary">{snippet}</p>
      )}
    </Link>
  )
}
